<template>
  <DetailsLayout
    type="transaction"
    :date="header?.date"
    :copyValue="header?.copyValue"
    :error="error"
    :icon="mainInfo?.icon"
  >
    <template #left>
      <MainInfo
        :title="mainInfo?.title"
        :subtitle="mainInfo?.subtitle"
        :details="mainInfo?.details"
        :copyValue="mainInfo?.copyValue"
        :amountData="mainInfo?.amountData"
        :loading="isLoading"
      />
    </template>
    <template #right>
      <template v-if="network === 'usdt'">
        <TokenTable :data="tokenTableData" />
      </template>
      <template v-if="network === 'bitcoin'">
        <TransactionDetailAccordion :data="tableData" :isLoading="isLoading" />
        <BitcoinDetailBlocks
          :inputs="inputs"
          :outputs="outputs"
          :network="network"
          :is-coinbase="data?.is_coinbase"
          :fee="data?.fee"
          :total-input="data?.total_input"
          :total-output="data?.total_output"
          :isLoading="isLoading"
        />
      </template>
      <template v-if="network === 'tron'">
        <TronDetails
          :details="data?.details"
          :block="data?.block"
          :fee="data?.fee"
          :status="data?.status"
          :isLoading="isLoading"
          network="tron"
        />
      </template>
      <template v-if="network === 'ton'">
        <TonDetails
          :details="data?.details"
          :block="data?.block"
          :fee="data?.fee"
          :status="data?.status"
          :isLoading="isLoading"
          network="ton"
        />
      </template>
      <template v-if="network === 'ethereum'">
        <EthereumDetails
          :details="data?.details"
          :block="data?.block"
          :fee="data?.fee"
          :status="data?.status"
          :isLoading="isLoading"
          network="ethereum"
        />
      </template>
    </template>
  </DetailsLayout>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { DetailsLayout } from "@/widgets";
import { MainInfo, TokenTable } from "@/features";
import TransactionDetailAccordion from "@/features/TransactionDetails/Common/ui/TransactionDetailAccordion.vue";
import BitcoinDetailBlocks from "@/features/TransactionDetails/Bitcoin/ui/BitcoinDetailBlocks.vue";
import TronDetails from "@/features/TransactionDetails/Tron/ui/TronDetails.vue";
import TonDetails from "@/features/TransactionDetails/TON/ui/TonDetails.vue";
import EthereumDetails from "@/features/TransactionDetails/Ethereum/EthereumDetails.vue";
import { transactionFormatter } from "@/shared/utils/formaters";
import { tokenTableData } from "@/app/mocks/address";
import { NETWORK_ENUMS } from "@/shared/helpers";
import { useLiveAge } from "@/shared/composables/useLiveAge";

// Store и route
const store = useStore();
const route = useRoute();

// Reactive data
const isLoading = ref(false);
const error = ref("");
const data = ref(null);

// Computed properties
const header = computed(() => data.value?.header);

const mainInfo = computed(() => data.value?.mainInfo);

const network = computed(() => route.params.network);

// Use live age for Bitcoin transactions
const { age: bitcoinLiveAge } = useLiveAge(
  computed(() => data.value?.block?.timestamp),
);

const tableData = computed(() => {
  if (network.value === NETWORK_ENUMS.bitcoin && data.value?.tableData) {
    // Replace the age value with live age for Bitcoin transactions
    return data.value.tableData.map((item) => {
      if (item.label === "Age") {
        return {
          ...item,
          value: { title: bitcoinLiveAge.value },
        };
      }
      return item;
    });
  }
  return data.value?.tableData;
});

const inputs = computed(() => data.value?.inputs || []);

const outputs = computed(() => data.value?.outputs || []);

const hash = computed(() => route.params.id);

const getUserData = computed(() => store.getters["user/getUserData"]);

// Methods
const GET_TRANSACTION = (params) =>
  store.dispatch("explorer/GET_TRANSACTION", params);

// Lifecycle
onMounted(async () => {
  isLoading.value = true;

  try {
    const response = await GET_TRANSACTION({
      network: network.value,
      hash: hash.value,
    });

    switch (network.value) {
      case NETWORK_ENUMS.bitcoin: {
        const formattedData = transactionFormatter(response);
        data.value = {
          ...formattedData,
          fee: response.fee,
          total_input: response.total_input,
          total_output: response.total_output,
          is_coinbase: !!(response.details && response.details?.is_coinbase),
        };
        console.log(data.value, "data.value");
        break;
      }
      case NETWORK_ENUMS.tron: {
        const { details, block, fee, status, tx_type } = response;

        const formattedBase = transactionFormatter(response);
        data.value = {
          ...formattedBase,
          details: details,
          block: block,
          fee: tx_type === "tron_trc10" ? {} : fee, //Hiding the amount and fee for tron_trc10
          status: status,
        };
        console.log(data.value, "data.value");
        break;
      }
      case NETWORK_ENUMS.ton: {
        const {
          details,
          block,
          fee,
          status,
          // tx_type
        } = response;

        const formattedBase = transactionFormatter(response);
        data.value = {
          ...formattedBase,
          details: details,
          block: block,
          fee: fee,
          status: status,
        };
        console.log(data.value, "data.value");

        break;
      }
      case NETWORK_ENUMS.ethereum: {
        const {
          details,
          block,
          fee,
          status,
          // tx_type
        } = response;
        console.log(details, "details");
        const formattedBase = transactionFormatter(response);
        console.log(formattedBase, "formattedBase");
        data.value = {
          ...formattedBase,
          details: details,
          block: block,
          fee: fee,
          status: status,
        };
        console.log(data.value, "data.value");

        break;
      }
      default: {
        data.value = response;
      }
    }
  } catch (error) {
    console.error("🔍 TransactionPage - Error loading transaction:", error);
    error.value =
      error?.response?.data?.error || error?.message || "An error occurred";
  } finally {
    isLoading.value = false;
  }

  window.emcd.AnalyticsService.track("page_view", {
    event_type: "page_view",
    product: "explorer",
    screen: "transaction",
    type: getUserData.value ? "internal_tx_view" : "external_tx_view",
    user_id: getUserData.value ? getUserData.value.userData.user_id : null,
    utm_source: route.query.utm_source || null,
    utm_medium: route.query.utm_medium || null,
    utm_campaign: route.query.utm_campaign || null,
    referrer: route.query.referrer || null,
  });
});
</script>
